import React, { useCallback, useMemo, useState } from 'react';
import { Form } from 'antd';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { FunctionalRootState } from '@share/utils';
import type { ILoginState } from '@share/store/slices';
import { useCertificateClubSiteId } from '@share/components';

import { CertificateTermsModal } from '../certificate-terms-modal';
import {
  CertificateHolderSection,
  EligibleCruiseLinesSection,
  FullPaymentDetailsSection,
  InstalmentPaymentFlowSection,
  PaymentMethodIntroSection,
  PaymentScheduleModal,
  PurchaseAgreementFooter,
} from './components';
import type { CertificatePaymentFormProps, PaymentFormValues } from './types';

import styles from './styles.module.scss';
import {
  useCertificatePurchaseSubmission,
  usePaymentFormDerived,
  usePaymentFormInitialValues,
  usePaymentFormRefs,
  usePaymentFormSyncEffects,
  usePaymentFormWatchedValues,
} from './hooks';
import { buildPaymentFormValidationRules } from './validation';
import { PaymentFormValidationProvider } from './payment-form-validation-context';

export const CertificatePaymentForm = ({
  certificateTemplate,
  returnUrl,
  stripePromise,
  redeemedruiseLines,
  onSuccess,
  variant = 'template',
  paymentToken,
  holderInitialValues,
  requiresInlineSignup = false,
}: CertificatePaymentFormProps): React.ReactElement => {
  const intl = useIntl();
  const validationRules = useMemo(
    () => buildPaymentFormValidationRules(intl, { variant, requiresInlineSignup }),
    [intl, variant, requiresInlineSignup],
  );

  const [termsOpen, setTermsOpen] = useState(false);
  const handleOpenTerms = useCallback(() => {
    setTermsOpen(true);
  }, []);
  const handleCloseTerms = useCallback(() => {
    setTermsOpen(false);
  }, []);

  const { user } = useSelector((state: FunctionalRootState) => state.loginStore) as ILoginState;
  const organizationId = useCertificateClubSiteId();

  const [form] = Form.useForm<PaymentFormValues>();
  const refs = usePaymentFormRefs();
  const watched = usePaymentFormWatchedValues(form);

  const certificateTotal = certificateTemplate?.totalPrice ?? 0;

  const derived = usePaymentFormDerived({ intl, form, certificateTotal, watched });
  const { initialValues } = usePaymentFormInitialValues({
    form,
    user,
    certificateTemplateTotalPrice: certificateTemplate?.totalPrice,
    holderOverride: holderInitialValues,
    requiresInlineSignup,
  });

  const { handlePlanChange } = usePaymentFormSyncEffects({
    form,
    certificateTotal,
    certificateTemplateTotalPrice: certificateTemplate?.totalPrice,
    watched,
    refs: {
      prevDownForSplitRef: refs.prevDownForSplitRef,
      prevSplitPayInFullRef: refs.prevSplitPayInFullRef,
      prevSplitInstalmentDownRef: refs.prevSplitInstalmentDownRef,
    },
  });

  const { submitting, handleSubmit, setCard1Group, setCard2Group } =
    useCertificatePurchaseSubmission({
      intl,
      form,
      certificateTemplate,
      returnUrl,
      organizationId,
      certificateTotal,
      onSuccess,
      variant,
      paymentToken,
      requiresInlineSignup,
      refs: {
        wrapperRef: refs.wrapperRef,
        card1GroupRef: refs.card1GroupRef,
        card2GroupRef: refs.card2GroupRef,
      },
    });

  return (
    <PaymentFormValidationProvider value={validationRules}>
      <div ref={refs.wrapperRef} className={styles.wrapper}>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          className={styles.form}
          disabled={submitting}
        >
          <CertificateHolderSection
            variant={variant}
            requiresInlineSignup={requiresInlineSignup}
            initialValues={{
              firstName: holderInitialValues?.firstName ?? initialValues.firstName,
              lastName: holderInitialValues?.lastName ?? initialValues.lastName,
              email: holderInitialValues?.email ?? initialValues.email,
            }}
          />

          <PaymentMethodIntroSection onPlanChange={handlePlanChange} />

          {derived.showInstalmentBlock && (
            <InstalmentPaymentFlowSection
              downPaymentRules={derived.downPaymentRules}
              instalmentSummary={derived.instalmentSummary}
              isSubmitting={submitting}
              showSplitInstalmentDown={derived.showSplitInstalmentDown}
              downForSplitRules={derived.downForSplitRules}
              splitDownRules1={derived.splitDownRules1}
              splitDownRules2={derived.splitDownRules2}
              splitDownLastRef={refs.splitDownLastRef}
              stripePromise={stripePromise}
              billingAddressSameAsFirst={watched.billingAddressSameAsFirst}
              onOpenPaymentSchedule={derived.handleOpenPaymentSchedule}
              setCard1Group={setCard1Group}
              setCard2Group={setCard2Group}
            />
          )}

          {!derived.hidePaymentDetailsForInstalments && (
            <FullPaymentDetailsSection
              showSplitPayInFull={derived.showSplitPayInFull}
              certificateTotal={certificateTotal}
              splitFullRules1={derived.splitFullRules1}
              splitFullRules2={derived.splitFullRules2}
              splitFullLastRef={refs.splitFullLastRef}
              stripePromise={stripePromise}
              billingAddressSameAsFirst={watched.billingAddressSameAsFirst}
              showSecondCard={derived.showSecondCard}
              cardOneSectionLabel={derived.cardOneSectionLabel}
              isSubmitting={submitting}
              setCard1Group={setCard1Group}
              setCard2Group={setCard2Group}
            />
          )}

          <hr className={styles.sectionDivider} />

          {redeemedruiseLines && <EligibleCruiseLinesSection lines={redeemedruiseLines} />}

          <PurchaseAgreementFooter
            agreementChargeLabel={derived.agreementChargeLabel}
            agreeCharge={watched.agreeCharge}
            agreeTerms={watched.agreeTerms}
            submitting={submitting}
            onOpenTerms={handleOpenTerms}
            onSubmit={handleSubmit}
          />
        </Form>

        <CertificateTermsModal
          termsAndConditionsContent={certificateTemplate?.termsAndConditionsContent}
          open={termsOpen}
          onClose={handleCloseTerms}
        />
        <PaymentScheduleModal
          open={derived.scheduleModalOpen}
          onClose={derived.handleClosePaymentSchedule}
          entries={derived.scheduleEntries}
          total={derived.scheduleTotal}
        />
      </div>
    </PaymentFormValidationProvider>
  );
};
